import React from 'react';
import './Guides.css';

export default function LightSwitchGuide() {
  return (
    <div className="guide-container">
      <h1 className="guide-header">💡 How to Wire a Light Switch with Arduino</h1>

      <div className="guide-body">
        <img
          src="/images/light-switch-guide.png"
          alt="Light switch circuit"
          className="guide-image"
        />

        <h2>💡 Pushbutton Light Switch with Arduino</h2>
        <p>
          This simple guide shows how to wire a pushbutton switch to an LED and use Arduino code to turn the LED
          on or off based on button presses. Perfect for beginners learning digital input/output concepts!
        </p>

        <h2>🧩 What You'll Need:</h2>
        <ul className="components-list">
          <li>1 × Arduino Nano or UNO</li>
          <li>1 × Breadboard</li>
          <li>1 × LED (any color)</li>
          <li>1 × 220Ω resistor</li>
          <li>1 × Pushbutton switch (normally open)</li>
          <li>Jumper wires</li>
        </ul>

        <h2>🔌 Wiring Guide:</h2>
        <div className="wiring-steps">
          <ol>
            <li>Connect the <strong>long leg (anode)</strong> of the LED to <strong>digital pin 8</strong> through a 220Ω resistor.</li>
            <li>Connect the <strong>short leg (cathode)</strong> of the LED to <strong>GND</strong>.</li>
            <li>Place the pushbutton across the breadboard center gap.</li>
            <li>Connect <strong>one side</strong> of the button to <strong>5V</strong>.</li>
            <li>Connect the <strong>other side</strong> of the button to <strong>digital pin 2</strong> and to <strong>GND through a 10kΩ pull-down resistor</strong>.</li>
          </ol>
        </div>

        <div className="warning-box">
          <p>⚠️ <strong>Important:</strong> If you're not using an external pull-down resistor, you can enable the internal pull-up resistor in the code (see alternative version below).</p>
        </div>

        <h2>💻 Sample Code:</h2>
        <div className="code-block">
          <pre>
{`int ledPin = 8;
int buttonPin = 2;
int buttonState = 0;

void setup() {
  pinMode(ledPin, OUTPUT);
  pinMode(buttonPin, INPUT);
}

void loop() {
  buttonState = digitalRead(buttonPin);

  if (buttonState == HIGH) {
    digitalWrite(ledPin, HIGH); // Turn LED on
  } else {
    digitalWrite(ledPin, LOW);  // Turn LED off
  }
}`}
          </pre>
        </div>

        <h2>🔁 Alternative (Using Internal Pull-up Resistor):</h2>
        <div className="code-block">
          <pre>
{`void setup() {
  pinMode(buttonPin, INPUT_PULLUP);
}`}
          </pre>
        </div>
        <p>
          In this version, the button reads <code>LOW</code> when pressed and <code>HIGH</code> when released,
          so you'll need to reverse the logic in the <code>loop()</code>.
        </p>

        <h2>🧪 What's Happening?</h2>
        <div className="explanation-box">
          <ul>
            <li>The Arduino constantly checks the state of the button pin using <code>digitalRead()</code></li>
            <li>When the button is pressed, the pin reads HIGH and turns the LED on</li>
            <li>When released, the pin reads LOW and the LED turns off</li>
            <li>The 220Ω resistor limits current to protect the LED from burning out</li>
          </ul>
        </div>

        <div className="success-box">
          <p>🎉 <strong>Congratulations!</strong> You've built your first interactive Arduino project. This is a great foundation for understanding digital input/output and forms the basis for more complex robotics projects!</p>
        </div>

        <h2>🚀 Next Steps:</h2>
        <p>Try these modifications to expand your learning:</p>
        <ul>
          <li>Add multiple LEDs that light up in sequence</li>
          <li>Use a toggle switch instead of momentary button</li>
          <li>Add a buzzer that sounds when the LED turns on</li>
          <li>Create a delay so the LED stays on for 3 seconds after button press</li>
        </ul>
      </div>
    </div>
  );
}